import { Link, useNavigate, useParams } from 'react-router-dom'
import { AdminLayout } from '../components/AdminLayout.js'
import { InvoiceBadge } from '../components/InvoiceBadge.js'
import { InvoiceActions } from '../components/InvoiceActions.js'
import { AdminApiError } from '../adminApi.js'
import { useInvoice } from '../hooks/useInvoices.js'
import { fmtDate, fmtDateTime, fmtMoney } from '../format.js'
import type { InvoiceRow } from '../types.js'

/** Ordered status timeline for the 3a invoice model. */
function timeline(invoice: InvoiceRow): Array<{
  label: string
  done: boolean
  detail?: string
}> {
  const created = {
    label: 'Created (unpaid)',
    done: true,
    detail: fmtDateTime(invoice.created_at),
  }
  if (invoice.status === 'void') {
    return [
      created,
      {
        label: 'Voided',
        done: true,
        detail: `${fmtDateTime(invoice.voided_at)}${
          invoice.void_reason ? ` — ${invoice.void_reason}` : ''
        }`,
      },
    ]
  }
  const paid = {
    label: 'Paid',
    done: invoice.status === 'paid',
    ...(invoice.paid_at ? { detail: fmtDateTime(invoice.paid_at) } : {}),
  }
  return [created, paid]
}

export function InvoiceDetail() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const query = useInvoice(id)

  if (query.isLoading) {
    return (
      <AdminLayout>
        <p className="admin-audit-meta">Loading invoice…</p>
      </AdminLayout>
    )
  }

  if (query.isError) {
    const notFound =
      query.error instanceof AdminApiError && query.error.status === 404
    return (
      <AdminLayout>
        <div className="admin-error-state">
          <h2>{notFound ? 'Invoice not found' : "Couldn't load invoice"}</h2>
          <button
            type="button"
            className="admin-btn"
            onClick={() => navigate('/admin/invoices')}
          >
            Back to invoices
          </button>
        </div>
      </AdminLayout>
    )
  }

  const invoice = query.data!.invoice

  return (
    <AdminLayout>
      <div className="admin-page-head">
        <div>
          <h1>{invoice.invoice_number}</h1>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              marginTop: 8,
              flexWrap: 'wrap',
            }}
          >
            <InvoiceBadge status={invoice.status} />
            <span className="admin-audit-meta">
              {fmtMoney(invoice.amount_centavos)}
            </span>
            <Link
              to={`/admin/tenants/${invoice.tenant_id}`}
              className="admin-audit-meta"
            >
              View tenant
            </Link>
          </div>
        </div>
        <InvoiceActions invoice={invoice} />
      </div>

      <div className="admin-section">
        <h3>Details</h3>
        <div className="admin-card">
          <dl className="admin-dl">
            <dt>Amount</dt>
            <dd>{fmtMoney(invoice.amount_centavos)}</dd>
            <dt>Plan tier at issue</dt>
            <dd style={{ textTransform: 'capitalize' }}>
              {invoice.plan_tier_at_issue}
            </dd>
            <dt>Billing cycle</dt>
            <dd style={{ textTransform: 'capitalize' }}>
              {invoice.billing_cycle}
            </dd>
            <dt>Period</dt>
            <dd>
              {fmtDate(invoice.period_start)} – {fmtDate(invoice.period_end)}
            </dd>
            {invoice.status === 'paid' && (
              <>
                <dt>Payment method</dt>
                <dd style={{ textTransform: 'capitalize' }}>
                  {invoice.payment_method ?? '—'}
                </dd>
                <dt>Payment reference</dt>
                <dd>{invoice.payment_reference || '—'}</dd>
                <dt>Paid at</dt>
                <dd>{fmtDateTime(invoice.paid_at)}</dd>
              </>
            )}
            {invoice.status === 'void' && (
              <>
                <dt>Void reason</dt>
                <dd>{invoice.void_reason || '—'}</dd>
                <dt>Voided at</dt>
                <dd>{fmtDateTime(invoice.voided_at)}</dd>
              </>
            )}
          </dl>
        </div>
      </div>

      <div className="admin-section">
        <h3>Status timeline</h3>
        <div className="admin-card">
          {timeline(invoice).map((step, i) => (
            <div key={i} className="admin-audit-row">
              <div className="admin-audit-time">
                {step.done ? '●' : '○'} Step {i + 1}
              </div>
              <div>
                <span className="admin-audit-action">{step.label}</span>
                {step.detail && (
                  <div className="admin-audit-meta">{step.detail}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  )
}
